import { motion } from "framer-motion";
import { Target, Banknote, Rocket } from "lucide-react";

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: Target,
      title: "Pick Your Battlefield",
      description:
        "Choose from thousands of live meme coins across Solana, Base, and Ethereum DEXs — or let GHOST surface the hottest movers.",
    },
    {
      number: "02",
      icon: Banknote,
      title: "Trade With Ghost Money",
      description:
        "Execute buys, sells, and limit orders in a simulated environment powered by real market data — no wallet connection required.",
    },
    {
      number: "03",
      icon: Rocket,
      title: "Go Live When You're Ready",
      description:
        "Review your performance, refine your edge, and step into real trading with a strategy that's already been battle-tested.",
    },
  ];

  return (
    <section
      id="how-it-works"
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="how-it-works-section"
    >
      {/* Background effects */}
      <div className="absolute top-0 right-0 w-64 md:w-96 h-64 md:h-96 bg-[#00D4FF] rounded-full mix-blend-multiply filter blur-[120px] md:blur-[150px] opacity-10" />
      <div className="absolute bottom-0 left-0 w-64 md:w-96 h-64 md:h-96 bg-[#8B5CF6] rounded-full mix-blend-multiply filter blur-[120px] md:blur-[150px] opacity-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-20"
        >
          <span className="inline-block px-3 md:px-4 py-2 rounded-full glass text-xs md:text-sm font-mono text-[#00D4FF] mb-3 md:mb-4">
            HOW IT WORKS
          </span>
          <h2 className="font-['Unbounded'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            Three Steps to
            <br />
            <span className="gradient-text">Smarter Trading</span>
          </h2>
        </motion.div>

        {/* Steps - Mobile: Stacked, Desktop: Alternating */}
        <div className="relative">
          {/* Connecting line - Desktop only */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#00D4FF]/50 via-[#8B5CF6]/50 to-transparent" />

          <div className="space-y-8 md:space-y-12 lg:space-y-24">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`flex flex-col lg:flex-row items-center gap-6 lg:gap-16 ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`step-${step.number}`}
              >
                {/* Number & Icon */}
                <div className="relative flex-shrink-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl md:rounded-3xl glass flex items-center justify-center ice-hover group">
                    <div className="absolute -top-3 -right-3 md:-top-4 md:-right-4 w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-[#00D4FF] to-[#8B5CF6] flex items-center justify-center shadow-[0_0_30px_rgba(0,212,255,0.5)]">
                      <span className="font-mono font-bold text-white text-xs md:text-sm">
                        {step.number}
                      </span>
                    </div>
                    <step.icon
                      size={36}
                      className="text-[#00D4FF] group-hover:text-white transition-colors duration-300 md:w-12 md:h-12"
                    />
                  </div>
                </div>

                {/* Content */}
                <div
                  className={`flex-1 text-center lg:text-left ${
                    index % 2 === 1 ? "lg:text-right" : ""
                  }`}
                >
                  <h3 className="font-['Unbounded'] text-xl md:text-2xl lg:text-3xl font-bold mb-3 md:mb-4 text-white">
                    {step.title}
                  </h3>
                  <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed max-w-lg mx-auto lg:mx-0">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
